import { useEffect, useRef, useState } from "react";
import React from "react";
import { nodeDataAutoSaveDynamic } from "../util/autosave/nodedata_autosave";

interface InputSize {
  width: string;
  height: string;
}

interface UseInputSizeTrackingOptions {
  nodeId: string | undefined;
  inputSize?: InputSize;
  collapsed?: boolean;
  edges: any[];
  flowId?: string;
  setNodeState: any;
  setSaving: any;
  setSchema: any;
  sizeKey?: string;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  textareaName?: string;
}

const getElementSize = (element: HTMLTextAreaElement): InputSize => {
  return {
    width: element.style.width || `${element.offsetWidth}px`,
    height: element.style.height || `${element.offsetHeight}px`,
  };
};

const applyInitialSize = (
  element: HTMLTextAreaElement,
  savedInputSize: InputSize | undefined,
  hasAppliedInitialSizeRef: React.MutableRefObject<boolean>,
) => {
  if (!hasAppliedInitialSizeRef.current && savedInputSize) {
    if (savedInputSize.width) {
      element.style.width = savedInputSize.width;
    }
    if (savedInputSize.height) {
      element.style.height = savedInputSize.height;
    }
    hasAppliedInitialSizeRef.current = true;
  }
};

const saveSizeIfChanged = (
  size: InputSize,
  savedInputSize: InputSize | undefined,
  saveParams: {
    edges: any[];
    nodeId: string;
    flowId?: string;
    setNodeState: any;
    setSaving: any;
    setSchema: any;
    sizeKey: string;
  },
) => {
  if (!saveParams.nodeId?.trim()) {
    return;
  }
  if (
    size.width !== savedInputSize?.width ||
    size.height !== savedInputSize?.height
  ) {
    nodeDataAutoSaveDynamic({
      newEdges: saveParams.edges,
      setNodeState: saveParams.setNodeState,
      id: saveParams.nodeId,
      setSaving: saveParams.setSaving,
      flowId: saveParams.flowId,
      objectData: { [saveParams.sizeKey]: size },
      setSchema: saveParams.setSchema,
    });
  }
};

export const useInputSizeTracking = ({
  nodeId,
  inputSize: savedInputSize,
  collapsed = false,
  edges,
  flowId,
  setNodeState,
  setSaving,
  setSchema,
  sizeKey = "inputSize",
  textareaRef,
  textareaName,
}: UseInputSizeTrackingOptions): InputSize | undefined => {
  const [inputSize, setInputSize] = useState(savedInputSize);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isResizingRef = useRef(false);
  const elementRef = useRef<HTMLTextAreaElement | null>(null);
  const hasAppliedInitialSizeRef = useRef(false);
  const lastNodeIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (savedInputSize) {
      setInputSize(savedInputSize);
    }
  }, [savedInputSize]);

  useEffect(() => {
    if (collapsed) return;
    if (!nodeId?.trim()) return;

    if (lastNodeIdRef.current !== nodeId) {
      hasAppliedInitialSizeRef.current = false;
      lastNodeIdRef.current = nodeId;
    }

    const findTextarea = (): HTMLTextAreaElement | null => {
      if (textareaRef?.current) {
        return textareaRef.current;
      }

      const selector = textareaName
        ? `[data-id="${nodeId}"] textarea[name="${textareaName}"]`
        : `[data-id="${nodeId}"] textarea`;
      return document.querySelector(selector) as HTMLTextAreaElement;
    };

    const createResizeCallback = (element: HTMLTextAreaElement) => {
      return () => {
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }

        isResizingRef.current = true;
        const currentSize = getElementSize(element);
        setInputSize(currentSize);

        resizeTimeoutRef.current = setTimeout(() => {
          if (isResizingRef.current) {
            const finalSize = getElementSize(element);
            saveSizeIfChanged(finalSize, savedInputSize, {
              edges,
              nodeId,
              flowId,
              setNodeState,
              setSaving,
              setSchema,
              sizeKey,
            });
            isResizingRef.current = false;
          }
        }, 500);
      };
    };

    const setupObserver = (element: HTMLTextAreaElement) => {
      elementRef.current = element;
      applyInitialSize(element, savedInputSize, hasAppliedInitialSizeRef);

      const resizeObserver = new ResizeObserver(createResizeCallback(element));
      resizeObserver.observe(element);
      return resizeObserver;
    };

    const initializeObserver = () => {
      const textarea = findTextarea();
      if (textarea) {
        return setupObserver(textarea);
      }
      return null;
    };

    let resizeObserver: ResizeObserver | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    resizeObserver = initializeObserver();
    if (!resizeObserver) {
      timeoutId = setTimeout(() => {
        resizeObserver = initializeObserver();
      }, 100);
    }

    return () => {
      if (
        (resizeTimeoutRef.current || isResizingRef.current) &&
        elementRef.current
      ) {
        const finalSize = getElementSize(elementRef.current);
        saveSizeIfChanged(finalSize, savedInputSize, {
          edges,
          nodeId,
          flowId,
          setNodeState,
          setSaving,
          setSchema,
          sizeKey,
        });
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      elementRef.current = null;
      isResizingRef.current = false;
    };
  }, [
    nodeId,
    collapsed,
    textareaRef,
    textareaName,
    savedInputSize,
    edges,
    flowId,
    setNodeState,
    setSaving,
    setSchema,
    sizeKey,
  ]);

  return inputSize || savedInputSize;
};
